/**
 * Shared layout component for authentication pages.
 * Provides consistent styling with the CK brand colors and design.
 */
import {
  Box,
  Card,
  Container,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ReactNode } from "react";
import { useAuthContext } from "../hooks/useAuthContext";
import { gettext } from "@services/i18n";
import CkLogoLight from "../../../images/CapturedKnowledgeHeaderLight.svg?react";
import { primitives, overlayOpacity } from "@theme/colors";

// Translation constants for extraction
const NEED_HELP_TEXT = gettext("Need help?");

// CK brand colors matching auth-pages.css - using theme primitives
const BRAND_COLORS = {
  gradientStart: primitives.primary[500], // #365D76
  gradientMiddle: primitives.primary[600], // #233E50
  gradientEnd: primitives.primary[700], // #162B37
  primary: primitives.primary[600], // #233E50
  primaryHover: primitives.primary[700], // #162B37
  link: primitives.secondary[500], // #2B6574
  linkHover: primitives.secondary[600], // #02424C
  text: {
    primary: "#2F3746",
    secondary: "#6C737F",
    muted: "#9DA4AE",
  },
  border: "#E5E7EB",
  error: "#F04438",
  inputBorder: "#D2D6DB",
  inputFocus: "#6366F1",
};

interface AuthLayoutProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
}

export function AuthLayout({ children, title, subtitle }: AuthLayoutProps) {
  const { currentLanguage, availableLanguages, csrfToken } = useAuthContext();

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = "/i18n/setlang/";

    const csrfInput = document.createElement("input");
    csrfInput.type = "hidden";
    csrfInput.name = "csrfmiddlewaretoken";
    csrfInput.value = csrfToken;
    form.appendChild(csrfInput);

    const nextInput = document.createElement("input");
    nextInput.type = "hidden";
    nextInput.name = "next";
    nextInput.value = window.location.pathname;
    form.appendChild(nextInput);

    const languageInput = document.createElement("input");
    languageInput.type = "hidden";
    languageInput.name = "language";
    languageInput.value = event.target.value;
    form.appendChild(languageInput);

    document.body.appendChild(form);
    form.submit();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        background: `linear-gradient(135deg, ${BRAND_COLORS.gradientStart} 0%, ${BRAND_COLORS.gradientMiddle} 50%, ${BRAND_COLORS.gradientEnd} 100%)`,
        fontFamily:
          "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      {/* Language Selector - Fixed position top right */}
      <FormControl
        size="small"
        sx={(theme) => ({
          position: "fixed",
          top: theme.spacing(4),
          right: theme.spacing(4),
          zIndex: 1000,
          minWidth: 100,
        })}
      >
        <Select
          value={currentLanguage}
          onChange={handleLanguageChange}
          sx={{
            color: "white",
            backgroundColor: alpha(primitives.mono.white, overlayOpacity.light),
            backdropFilter: "blur(10px)",
            border: `1px solid ${alpha(primitives.mono.white, overlayOpacity.heavy)}`,
            borderRadius: 2,
            fontSize: "0.875rem",
            fontWeight: 500,
            fontFamily: "'Montserrat', sans-serif",
            "& .MuiSelect-icon": {
              color: "white",
            },
            "&:hover": {
              backgroundColor: alpha(
                primitives.mono.white,
                overlayOpacity.strong,
              ),
              borderColor: alpha(primitives.mono.white, 0.5),
            },
            "&.Mui-focused": {
              borderColor: alpha(primitives.mono.white, 0.6),
              boxShadow: `0 0 0 3px ${alpha(primitives.mono.white, overlayOpacity.light)}`,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
          data-testid="language-selector"
        >
          {availableLanguages.map((lang) => (
            <MenuItem key={lang.code} value={lang.code}>
              {lang.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Main Content */}
      <Container
        maxWidth={false}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 2,
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 560,
            borderRadius: 4,
            boxShadow: 3,
            overflow: "hidden",
          }}
        >
          {/* Header with Logo */}
          <Box
            sx={(theme) => ({
              padding: theme.spacing(5, 5, 3),
              textAlign: "center",
              backgroundColor: "white",
            })}
          >
            <Box
              sx={{
                maxWidth: 370,
                margin: "0 auto",
                display: "block",
              }}
              data-testid="auth-logo"
            >
              <CkLogoLight
                style={{ width: "100%", height: "auto" }}
                aria-label="Captured Knowledge"
              />
            </Box>
            {title && (
              <Typography
                variant="h5"
                sx={(theme) => ({
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  color: "#222",
                  marginTop: theme.spacing(3),
                  marginBottom: theme.spacing(1),
                  fontFamily: "'Montserrat', sans-serif",
                })}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: BRAND_COLORS.text.secondary,
                  margin: 0,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {/* Content */}
          {children}

          {/* Footer */}
          <Box
            sx={(theme) => ({
              padding: theme.spacing(0, 5, 5),
              backgroundColor: "white",
            })}
          >
            <Box
              sx={(theme) => ({
                marginTop: theme.spacing(2),
                paddingTop: theme.spacing(2),
                borderTop: `1px solid ${BRAND_COLORS.border}`,
                textAlign: "center",
                fontSize: "0.8125rem",
                color: BRAND_COLORS.text.secondary,
              })}
            >
              {NEED_HELP_TEXT}{" "}
              <Box
                component="a"
                href="mailto:support@capturedknowledge.ai"
                sx={{
                  color: BRAND_COLORS.link,
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                support@capturedknowledge.ai
              </Box>
            </Box>
          </Box>
        </Card>
      </Container>
    </Box>
  );
}

export { BRAND_COLORS };
